/**
 * 金额的展示与解析（`05-ui-design-system.md` §7.8「金额、时间与编号格式」）。
 *
 * 规范原文：**前缀 `¥` + 两位小数 + 千分位：`¥1,280.00`**；界面不写「元」，只写 `¥`。
 *
 * ## 这个模块是**全 workspace 唯一**的金额实现
 *
 * Phase 5 Task 22 把三处实现收成一处：`apps/shop` 的 `lib/money.ts` 与
 * `apps/tenant` 的 `lib/schemeForm.ts` 都改为**转发**（`formatCents = formatYuan`），
 * `CartBar` / `ProductListItem` 里自己拼金额串的写法改为调用 `formatYuan`。
 * 两个应用各自的 spec 用**函数引用相等**钉住转发关系。
 *
 * ## 两个方向的转换各有「坏值」口径
 *
 * | 函数 | 坏值（非数值 / 空串） | 用途 |
 * |---|---|---|
 * | `yuanFromCents` / `formatYuan` | 不会失败（输入是数） | 分 → 展示串 |
 * | `centsFromYuan` | **`null`** | **展示层**：与「字段缺失」走同一分支隐藏金额 |
 * | `centsFromYuanOrZero` | `0` | **算术层**：价格表、行金额、缓存的实付单价、浮点元转分 |
 *
 * 分开的理由是审查指出的一处真实不一致：`totalAmount === null`（字段缺失）时页面**不显示**
 * 金额，而一个坏字符串经 `centsFromYuan` 折成 `0` 后却显示成 `¥0.00` —— 同一件事
 * （「没有可显示的金额」）在界面上有两种相反的待遇。返回 `null` 之后两者合并。
 */

/**
 * 分 → 展示串：两位小数 + **千分位**（`1280` → `"12.80"`，`128000` → `"1,280.00"`）。
 *
 * 不用运行时的本地化数字格式（`toLocale` 系列）：它的输出依赖运行时的 ICU 数据
 * （不同 Node / 浏览器 / 精简镜像上分组符可能不同），而金额格式是**规范硬性规定**的一项，
 * 不该随环境漂移。手工分组只有一行正则，且可在 jsdom 下直接断言。
 *
 * 负号留在千分位之外（`-1,280.00`）：退款/冲减场景会用到这里。
 */
export function yuanFromCents(cents: number): string {
  const value = Number.isFinite(cents) ? cents : 0;
  const negative = value < 0;
  // 分是整数，`/100` 天然只有两位小数；`toFixed(2)` 兜住调用方传入非整数分的情况
  const [whole = '0', fraction = '00'] = (Math.abs(value) / 100).toFixed(2).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}.${fraction}`;
}

/**
 * `¥` + `yuanFromCents`。**只有前缀这一件事**，没有任何分组逻辑（分组只有一份）。
 *
 * 负数的符号提到 `¥` 之前（`-¥1,280.00`）：这是既有的 `apps/tenant` 口径，
 * 也是财务语境的通行写法（`¥-1,280.00` 读起来是错的）。
 */
export function formatYuan(cents: number): string {
  return cents < 0 ? `-¥${yuanFromCents(-cents)}` : `¥${yuanFromCents(cents)}`;
}

/**
 * 元 → 分（整数）。`yuanFromCents` 的反方向，只给「服务端给的是元、而算式要求分」的地方用。
 *
 * 为什么需要它：网格的 `price` 是**字符串十进制元**（契约 §1.7），而促销预览、行金额、
 * 复核上限全部按**整数分**算（服务端 `toCents` 的同一口径）。浮点元参与比较与乘法会出现
 * `0.07 × 3 = 0.21000000000000002` 这类结果，`.toFixed(2)` 之前就会分叉。
 *
 * `Math.round` 而不是 `Math.floor`：`Number('0.07') * 100 === 7.000000000000001`（浮点误差），
 * 向下取整会把它变成 6 分。两位小数的十进制元乘 100 之后与整数的差远小于 0.5，
 * 因此 `round` 是安全的；字符串形态先转数值，数值形态（调用方自己构造的格子）直接乘。
 *
 * **坏值返回 `null`**（不是 `0`）：`null` 与 `0` 是两件事 —— `0` 是「这件商品真的免费」，
 * `null` 是「这个值读不出来」。展示层据此隐藏金额（与 `totalAmount === null` 同一分支），
 * 而不是把坏数据说成 `¥0.00`。需要参与算术的调用方用 `centsFromYuanOrZero`。
 */
export function centsFromYuan(price: string | number): number | null {
  if (typeof price === 'number') {
    return Number.isFinite(price) ? Math.round(price * 100) : null;
  }
  // 空串/空白串 `Number('')` 是 0 —— 那是「没填」而不是「零元」，必须挡掉
  if (typeof price !== 'string' || price.trim() === '') return null;
  const value = Number(price);
  return Number.isFinite(value) ? Math.round(value * 100) : null;
}

/**
 * 与 `centsFromYuan` 同源，但把坏值折成 `0` —— **只给算术用**。
 *
 * 三处调用方都是「取最小 / 求和」这类算式（`cart/cartView.ts` 的价格表、购物车与结算页的
 * 行金额、订单缓存的 `unitPriceCents`）：那里需要一个数，而服务端仍会在下单时重新计价
 * （结算页上写着「最终优惠以提交订单时服务端计价为准」）。
 *
 * 名字刻意长：读到这里的人应当先问「我是不是该用 `null` 那一支」。
 */
export function centsFromYuanOrZero(price: string | number): number {
  return centsFromYuan(price) ?? 0;
}
